import { createMachine } from 'xstate';

import shared from 'common/bento/shared';
import { AnyMachineEvent } from 'features/Bento/types/Abstract';

import locales from '../../../locales';
import actions from './actions';
import guards, { Guard } from './guards';
import model, { Context } from './model';
import services, { Service } from './services';

export enum State {
  INIT = 'init',
  WELCOME = 'intro',
  VERIFICATION = 'verification',
  CAPITAL_DEPOSIT_AGREEMENT = 'capital-deposit-agreement',
  AGREEMENT = 'agreement',
  CAPITAL_DEPOSIT_PLAN_CHOICE = 'capital-deposit-plan-choice',
  SEND_APPLICATION = 'send-application',
  CAPITAL_DEPOSIT_NEXT_STEPS = 'capital-deposit-next-steps',
  BENEFICIARIES_DOCS = 'beneficiaries-docs',
  DOWNLOAD_SHINE_MESSAGE_SENT = 'download-shine-message-sent',
}

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'invited-account-holder-capital-deposit',

    initial: State.INIT,

    on: {
      '*': {
        actions: shared.actions.unhandled(),
      },
    },

    states: {
      [State.INIT]: {
        always: [
          {
            cond: Guard.IsApplicationSent,
            target: State.CAPITAL_DEPOSIT_NEXT_STEPS,
          },
          {
            cond: Guard.HasStartedApplication,
            target: State.VERIFICATION,
          },
          { target: State.WELCOME },
        ],
      },

      [State.WELCOME]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: State.VERIFICATION,
          PREV: undefined,
        },
      },

      [State.VERIFICATION]: {
        entry: shared.actions.trackView(),

        on: {
          NEXT: State.CAPITAL_DEPOSIT_AGREEMENT,
          PREV: State.WELCOME,
        },
      },

      [State.CAPITAL_DEPOSIT_AGREEMENT]: {
        always: {
          cond: Guard.HasSignedCapitalDepositAgreement,
          target: State.AGREEMENT,
        },

        on: {
          NEXT: State.AGREEMENT,
          PREV: State.VERIFICATION,
        },
      },

      [State.AGREEMENT]: {
        always: {
          cond: Guard.HasSignedCguAgreement,
          target: State.CAPITAL_DEPOSIT_PLAN_CHOICE,
        },

        on: {
          NEXT: State.CAPITAL_DEPOSIT_PLAN_CHOICE,
          PREV: State.VERIFICATION,
        },
      },

      [State.CAPITAL_DEPOSIT_PLAN_CHOICE]: {
        always: {
          cond: Guard.IsCapitalDepositPaid,
          target: State.SEND_APPLICATION,
        },

        on: {
          NEXT: State.SEND_APPLICATION,
          PREV: State.VERIFICATION,
        },
      },

      [State.SEND_APPLICATION]: {
        invoke: {
          id: 'submitApplication',
          onDone: {
            actions: [
              shared.actions.track({
                name: 'submitApplication',
                properties: {
                  isCapitalDeposit: true,
                  isPrimaryApplicant: false,
                  isShineStart: false,
                  proofOfAddressSentByEmail: false,
                },
              }),
            ],
            target: State.CAPITAL_DEPOSIT_NEXT_STEPS,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.VERIFICATION,
          },

          src: Service.SubmitApplication,
        },
      },

      [State.CAPITAL_DEPOSIT_NEXT_STEPS]: {
        entry: shared.actions.trackView(),

        on: {
          DOWNLOAD_SHINE: State.DOWNLOAD_SHINE_MESSAGE_SENT,
          GO_BENEFICIARIES_DOCS: State.BENEFICIARIES_DOCS,
          PREV: {
            actions: shared.actions.toast({
              status: 'error',
              title: locales.toast.definitiveStep,
            }),
          },
        },
      },

      [State.BENEFICIARIES_DOCS]: {
        entry: shared.actions.trackView(),
        on: {
          PREV: State.CAPITAL_DEPOSIT_NEXT_STEPS,
          'verification-home': State.CAPITAL_DEPOSIT_NEXT_STEPS,
        },
      },

      [State.DOWNLOAD_SHINE_MESSAGE_SENT]: {
        entry: shared.actions.trackView(),
        on: {
          PREV: State.CAPITAL_DEPOSIT_NEXT_STEPS,
        },
      },
    },
  },

  {
    actions,
    guards,
    services,
  },
);

export default machine;
